// What this browser holds for an account across all its shows: discovering unsent work from the
// dashboard, packaging recovery data at sign-out, and explicit erasure. Database names carry the
// account id, so one account's tools never open another account's data.

import { recordKey } from "../format/ops.ts";
import type { AnyRecord, Doc, RecordType } from "../format/types.ts";
import { forgetAccount } from "./account.ts";
import { dbName, openDb, type PendingEntry, type Proposal, type RecoveryBase, request, type ShowSnapshot } from "./idb.ts";
import { clearPdfCaches } from "./offline.ts";

export interface LocalShow {
  showId: string;
  title: string;
  pending: number;
  held: number;
  proposals: number;
  drafts: number;
  /** A full offline copy (snapshot) is stored. */
  prepared: boolean;
  revision: number;
  role: ShowSnapshot["role"] | null;
}

const PREFIX = (accountId: string) => `q2q-a.${accountId}.s.`;

async function databaseNames(): Promise<string[]> {
  if (!indexedDB.databases) return [];
  return (await indexedDB.databases()).map((d) => d.name ?? "").filter(Boolean);
}

async function readAll(d: IDBDatabase): Promise<{ pending: PendingEntry[]; proposals: Proposal[]; drafts: unknown[]; snapshot?: ShowSnapshot; recovery?: RecoveryBase }> {
  const [pending, proposals, drafts, snapshot, recovery] = await Promise.all([
    request<PendingEntry[]>(d, ["pending"], "readonly", (t) => t.objectStore("pending").getAll()),
    request<Proposal[]>(d, ["proposals"], "readonly", (t) => t.objectStore("proposals").getAll()),
    request<unknown[]>(d, ["drafts"], "readonly", (t) => t.objectStore("drafts").getAll()),
    request<ShowSnapshot>(d, ["snapshot"], "readonly", (t) => t.objectStore("snapshot").get("show")),
    request<RecoveryBase>(d, ["meta"], "readonly", (t) => t.objectStore("meta").get("recovery")),
  ]);
  return { pending: pending ?? [], proposals: proposals ?? [], drafts: drafts ?? [], snapshot, recovery };
}

/** Every show this account has data for on this device, with counts only (no content). */
export async function localShows(accountId: string): Promise<LocalShow[]> {
  const out: LocalShow[] = [];
  for (const name of await databaseNames()) {
    if (!name.startsWith(PREFIX(accountId))) continue;
    const showId = name.slice(PREFIX(accountId).length);
    const d = await openDb(name);
    try {
      const all = await readAll(d);
      out.push({
        showId,
        title: all.snapshot?.title ?? all.recovery?.title ?? "Show",
        pending: all.pending.filter((e) => !e.held).length,
        held: all.pending.filter((e) => e.held).length,
        proposals: all.proposals.length,
        drafts: all.drafts.length,
        prepared: !!all.snapshot?.doc,
        revision: all.snapshot?.revision ?? all.recovery?.revision ?? 0,
        role: all.snapshot?.role ?? null,
      });
    } finally {
      d.close();
    }
  }
  return out;
}

/** Tell this account's other tabs (storage event) to stop and leave their show. */
function announceSignOut(accountId: string): void {
  localStorage.setItem(SIGNED_OUT, JSON.stringify({ accountId, at: Date.now() }));
}

/** The localStorage key other tabs watch for this browser's sign-outs. */
export const SIGNED_OUT = "q2q-signed-out";

export const unsent = (s: LocalShow) => s.pending + s.held + s.proposals + s.drafts;

/** The records unsent work refers to, from the snapshot: enough to review it after sign-out. */
function recoveryBase(snapshot: ShowSnapshot | undefined, pending: PendingEntry[], proposals: Proposal[]): RecoveryBase {
  const doc: Doc | null = snapshot?.doc ?? null;
  const wanted = new Map<string, { type: RecordType; key: string }>();
  for (const e of [...pending, ...proposals.map((p) => p.entry)]) {
    for (const op of e.ops) {
      const key = op.op === "put" ? recordKey(op.type, op.record) : op.key;
      wanted.set(`${op.type}/${key}`, { type: op.type, key });
    }
  }
  const records: RecoveryBase["records"] = [];
  for (const { type, key } of wanted.values()) {
    let record: AnyRecord | null = null;
    if (doc) {
      if (type === "project") record = doc.project;
      else record = ((doc[type] as AnyRecord[]).find((r) => recordKey(type, r) === key) ?? null);
    }
    records.push({ type, key, record });
  }
  return { title: snapshot?.title ?? "Show", revision: snapshot?.revision ?? 0, generation: snapshot?.generation ?? 0, records, keptAt: Date.now() };
}

/**
 * Sign-out: remove downloaded PDFs and acknowledged copies for this account; keep a minimal
 * recovery package (unsent edits held for review, proposals, drafts, base records) where there
 * is unsent work. Databases with nothing unsent are deleted entirely.
 */
export async function packageForSignOut(accountId: string): Promise<{ kept: number }> {
  let kept = 0;
  for (const name of await databaseNames()) {
    if (!name.startsWith(PREFIX(accountId))) continue;
    const d = await openDb(name);
    let remove = false;
    try {
      const all = await readAll(d);
      const work = all.pending.length + all.proposals.length + all.drafts.length;
      if (!work) remove = true;
      else {
        kept += work;
        const base = all.recovery ?? recoveryBase(all.snapshot, all.pending, all.proposals);
        await request(d, ["pending", "snapshot", "meta"], "readwrite", (t) => {
          for (const e of all.pending) t.objectStore("pending").put({ ...e, held: "signed-out" });
          t.objectStore("snapshot").delete("show");
          t.objectStore("meta").put(base, "recovery");
        });
      }
    } finally {
      d.close();
    }
    if (remove) await deleteDb(name);
  }
  await clearPdfCaches(accountId);
  announceSignOut(accountId);
  forgetAccount();
  return { kept };
}

function deleteDb(name: string): Promise<void> {
  const { promise, resolve, reject } = Promise.withResolvers<void>();
  const req = indexedDB.deleteDatabase(name);
  req.onsuccess = () => resolve();
  req.onerror = () => reject(req.error);
  // Another tab may still have it open: q2cue tabs close their connection on versionchange (and leave
  // the show on the sign-out/erase broadcast), after which the delete completes. Never report
  // erasure before it has happened.
  const timer = setTimeout(() => reject(new Error("Another q2cue tab is still using this show's storage. Close it and try again.")), 15_000);
  void promise.finally(() => clearTimeout(timer)).catch(() => {});
  return promise;
}

/** Remove acknowledged offline copies only; never discard work created on this browser. */
export async function removeOfflineCopies(accountId: string): Promise<void> {
  if (!indexedDB.databases) throw new Error("This browser can't check offline storage safely.");
  const shows = await localShows(accountId);
  const message = "Save or resolve all unsent changes, review items and drafts before removing offline copies.";
  if (shows.some((s) => unsent(s) > 0)) throw new Error(message);
  for (const show of shows) {
    const d = await openDb(dbName(accountId, show.showId));
    let blocked = false;
    try {
      // Check and clear in one transaction. Another tab can queue work after the dashboard
      // loaded; deleting its database would lose that work. Leave queues and settings intact.
      await request(d, ["pending", "proposals", "drafts", "snapshot", "meta"], "readwrite", (t) => {
        let remaining = 3;
        for (const store of ["pending", "proposals", "drafts"]) {
          const count = t.objectStore(store).count();
          count.onsuccess = () => {
            blocked ||= count.result > 0;
            if (--remaining === 0 && !blocked) {
              t.objectStore("snapshot").clear();
              t.objectStore("meta").delete("recovery");
            }
          };
        }
      });
    } finally {
      d.close();
    }
    if (blocked) throw new Error(message);
  }
  await clearPdfCaches(accountId);
}

/** Explicit erasure of everything this account keeps on this device (or one show of it). */
export async function eraseLocal(accountId: string, showId?: string): Promise<void> {
  if (!showId) announceSignOut(accountId);
  for (const name of await databaseNames()) {
    if (showId ? name === dbName(accountId, showId) : name.startsWith(PREFIX(accountId))) await deleteDb(name);
  }
  await clearPdfCaches(accountId, showId);
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const k = localStorage.key(i) ?? "";
    const scoped = showId ? k.endsWith(`.${accountId}.${showId}`) : k.includes(`.${accountId}`);
    if (scoped) localStorage.removeItem(k);
  }
  if (!showId) forgetAccount();
}

/** Counts of every account's q2cue data on this device, for a shared device's sign-in page. No content. */
export async function deviceSummary(): Promise<{ accounts: number; databases: number }> {
  const names = (await databaseNames()).filter((n) => n.startsWith("q2q-a."));
  return { accounts: new Set(names.map((n) => n.split(".")[1])).size, databases: names.length };
}

/** Remove every account's q2cue data from this browser (sign-in page, with confirmation). */
export async function eraseDevice(): Promise<void> {
  for (const name of await databaseNames()) if (name.startsWith("q2q-a.")) await deleteDb(name);
  if ("caches" in globalThis) for (const n of await caches.keys()) if (n.startsWith("q2q-pdf.")) await caches.delete(n);
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const k = localStorage.key(i) ?? "";
    if (k.startsWith("q2q-")) localStorage.removeItem(k);
  }
}

/** A JSON file with a show's unsent work and its base records, readable without q2cue. */
export async function downloadRecovery(accountId: string, showId: string): Promise<void> {
  const d = await openDb(dbName(accountId, showId));
  let data: unknown;
  try {
    const all = await readAll(d);
    data = {
      q2q: "unsaved changes",
      showId,
      title: all.snapshot?.title ?? all.recovery?.title ?? "",
      revision: all.snapshot?.revision ?? all.recovery?.revision ?? null,
      savedAt: new Date().toISOString(),
      entries: all.pending.map((e) => ({ id: e.id, label: e.label, at: new Date(e.at).toISOString(), generation: e.generation, held: e.held ?? null, ops: e.ops, before: e.before })),
      proposals: all.proposals.map((p) => ({ label: p.entry.label, reason: p.reason, message: p.message, ops: p.entry.ops, before: p.entry.before, current: p.records })),
      drafts: all.drafts,
      base: all.recovery?.records ?? null,
    };
  } finally {
    d.close();
  }
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `q2cue-recovery-${showId}-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
}
